'use client';

import { Box, Divider, Skeleton, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { SxProps, Theme } from '@mui/material/styles';
import { useTranslate } from '@tolgee/react';
import { useState } from 'react';

interface WeChatQRCodesProps {
  containerStyle?: React.CSSProperties;
  containerSx?: SxProps<Theme>;
  publicQrSrc: string;
  groupQrSrc: string;
}

export default function WeChatQRCodes({ containerStyle, containerSx, publicQrSrc, groupQrSrc }: WeChatQRCodesProps) {
  const { t } = useTranslate();
  const [publicQrLoaded, setPublicQrLoaded] = useState(false);
  const [groupQrLoaded, setGroupQrLoaded] = useState(false);

  // 基础颜色和样式
  const wechatColor = '#07C160';
  const borderColor = alpha(wechatColor, 0.3);

  // 共享的QR码容器样式
  const qrCodeStyle = {
    width: '100%',
    maxWidth: '180px',
    position: 'relative',
    aspectRatio: '1/1',
    borderRadius: '10px',
    overflow: 'hidden',
    boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
    border: '1px solid',
    borderColor,
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    '@media (hover: hover)': {
      '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: '0 14px 24px rgba(0,0,0,0.15)',
      },
    },
  };

  // 共享的图片样式
  const imgStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '8px',
    position: 'absolute',
    top: 0,
    left: 0,
    transition: 'opacity 0.4s ease',
  };

  // 共享的标题样式
  const titleStyle = {
    fontWeight: 600,
    color: '#ffffff',
    fontSize: '0.95rem',
    position: 'relative',
    display: 'inline-block',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: -6,
      left: '25%',
      width: '50%',
      height: 2,
      borderRadius: '2px',
      backgroundColor: alpha(wechatColor, 0.7),
    },
  };

  // 共享的描述样式
  const descStyle = {
    color: '#ffffff',
    fontSize: '0.85rem',
    textAlign: 'center',
    lineHeight: 1.4,
    opacity: 0.8,
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          justifyContent: 'space-between',
          flexDirection: 'row',
          width: '100%',
          alignItems: 'flex-start', // 顶部对齐
          ...containerSx,
        }}
        style={containerStyle}
      >
        {/* 公众号 */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            flex: 1,
          }}
        >
          {/* 标题区域 - 自适应高度但居中显示 */}
          <Box
            sx={{
              minHeight: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1,
              textAlign: 'center',
              width: '100%',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                ...titleStyle,
                textAlign: 'center',
                lineHeight: 1.3,
              }}
            >
              {t('wechat.official_account')}
            </Typography>
          </Box>

          {/* 二维码区域 */}
          <Box sx={qrCodeStyle}>
            {!publicQrLoaded && (
              <Skeleton
                variant="rounded"
                animation="wave"
                sx={{ width: '100%', height: '100%', borderRadius: '8px' }}
              />
            )}
            <Box component="img" src={publicQrSrc} alt={t('wechat.official_account')} sx={imgStyle} />
          </Box>

          {/* 描述区域 - 自适应高度但居中显示 */}
          <Box
            sx={{
              minHeight: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: 1.5,
              textAlign: 'center',
              width: '100%',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                ...descStyle,
                textAlign: 'center',
                lineHeight: 1.4,
              }}
            >
              {t('wechat.official_account_desc')}
            </Typography>
          </Box>
        </Box>

        <Divider
          orientation="vertical"
          flexItem
          sx={{
            my: 2,
            borderColor: alpha(wechatColor, 0.2),
          }}
        />

        {/* 显卡赚钱群 */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            flex: 1,
          }}
        >
          {/* 标题区域 - 自适应高度但居中显示 */}
          <Box
            sx={{
              minHeight: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1,
              textAlign: 'center',
              width: '100%',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                ...titleStyle,
                textAlign: 'center',
                lineHeight: 1.3,
              }}
            >
              {t('wechat.gpu_group_title')}
            </Typography>
          </Box>

          {/* 二维码区域 */}
          <Box sx={qrCodeStyle}>
            {!groupQrLoaded && (
              <Skeleton
                variant="rounded"
                animation="wave"
                sx={{ width: '100%', height: '100%', borderRadius: '8px' }}
              />
            )}
            <Box component="img" src={groupQrSrc} alt={t('wechat.gpu_group_title')} sx={imgStyle} />
          </Box>

          {/* 描述区域 - 自适应高度但居中显示 */}
          <Box
            sx={{
              minHeight: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: 1.5,
              textAlign: 'center',
              width: '100%',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                ...descStyle,
                textAlign: 'center',
                lineHeight: 1.4,
              }}
            >
              {t('wechat.gpu_group_desc')}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* 扫码提示 */}
      <Box
        sx={{
          width: '100%',
          mt: 2,
          pt: 2,
          textAlign: 'center',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -1,
            left: '10%',
            right: '10%',
            height: '2px',
            background: `linear-gradient(to right, transparent, ${alpha(wechatColor, 0.4)}, transparent)`,
          },
        }}
      >
        <Typography
          variant="body2"
          component="div"
          sx={{
            color: 'rgba(255,255,255,0.8)',
            fontSize: '0.85rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.8,
          }}
        >
          <Box
            component="span"
            sx={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: alpha(wechatColor, 0.2),
              color: wechatColor,
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
          </Box>
          <span>{t('wechat.scan_instruction')}</span>
        </Typography>
      </Box>
    </Box>
  );
}
